import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Plus, Search, Edit, Trash2, Eye, User, Users, Mail, Phone, 
  MapPin, Building, Building2, CreditCard, ChevronLeft, ChevronRight, 
  X, AlertCircle, CheckCircle, XCircle, Filter, Download, Upload, 
  Calendar, FileText, DollarSign, TrendingUp, TrendingDown, Clock, 
  Settings, Package, ShoppingBag, Smartphone, Copy, Star, Sparkles,
  Percent, Tag, Boxes, AlertTriangle, Coffee, BarChart3,
  ShoppingCart, LogOut, Menu, FileSpreadsheet,
  Trash, Truck
} from 'lucide-react';
import {
  solicitudesService,
  solicitudesArchivosService,
  articulosService,
  serviciosService,
  subserviciosService,
  categoriasSolicitudService,
  empresasService,
  clientesService,
  usuariosService,
  solicitudesArticulosService,
  UPLOADS_BASE_URL
} from '../../services/api';
import $ from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';
import ArticuloItem from '../common/ArticuloItem';
import useModal from '../../hooks/useModal';

// ==================== COMPONENTE MANAGER DE ARTÍCULOS ====================
const ArticulosSolicitudManager = ({
  articulos = [],
  onArticulosChange,
  articulosDisponibles = [],
  notify
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [articulosActuales, setArticulosActuales] = useState(articulos);

  // Sincronizar con prop externa
  useEffect(() => {
    setArticulosActuales(articulos);
  }, [articulos]);

  // Preparar opciones para el selector de artículos
  const opcionesArticulos = articulosDisponibles.map(art => ({
    value: art.id,
    label: `${art.nombre} ${art.tipo === 'articulo' ? `(Stock: ${art.stock_actual || 0})` : '(Servicio)'}`,
    tipo: art.tipo,
    stock: art.stock_actual,
    datos: art
  }));

  // Agregar artículo/servicio a la lista
  const handleAgregarArticulo = () => {
    if (!selectedItem) return;

    // Verificar si ya está en la lista
    const yaExiste = articulosActuales.some(
      art => art.id_articulo === selectedItem.value
    );

    if (yaExiste) {
      notify('warning', 'Atención', 'Este artículo/servicio ya está agregado');
      return;
    }

    const nuevoArticulo = {
      id_articulo: selectedItem.value,
      nombre: selectedItem.datos.nombre,
      tipo: selectedItem.tipo,
      stock_actual: selectedItem.stock,
      stock: selectedItem.stock,
      cantidad: 1,
      status: 'solicitud_nueva',
      via: '',
      tiempo_entrega: '',
      observacion: ''
    };

    const nuevosArticulos = [...articulosActuales, nuevoArticulo];
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
    setSelectedItem(null);
  };

  // ⭐ Actualizar campo de un artículo específico
  const updateArticuloField = (index, field, value) => {
    const nuevosArticulos = [...articulosActuales];
    nuevosArticulos[index] = {
      ...nuevosArticulos[index],
      [field]: value
    };
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
  };

  // ⭐ Eliminar artículo de la lista
  const removeArticulo = (index) => {
    const nuevosArticulos = articulosActuales.filter((_, i) => i !== index);
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
  };

  return (
    <div className="space-y-4">
      {/* 🔍 BUSCADOR DE ARTÍCULOS */}
      <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
        <label className="block text-gray-700 font-medium mb-2">
          🔍 Buscar y Seleccionar Artículos/Servicios
        </label>
        <div className="flex gap-2">
          <select
            value={selectedItem ? selectedItem.value : ''}
            onChange={(e) => {
              const opt = opcionesArticulos.find(o => String(o.value) === e.target.value);
              setSelectedItem(opt || null);
            }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Buscar artículo o servicio...</option>
            {opcionesArticulos.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAgregarArticulo}
            disabled={!selectedItem}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            ➕ Agregar
          </button>
        </div>
      </div>

      {/* 📦 LISTA DE ARTÍCULOS USANDO ArticuloItem */}
      {articulosActuales.length > 0 ? (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Artículos/Servicios Seleccionados ({articulosActuales.length})
          </h4>
          
          {/* ⭐ AQUÍ SE USA ArticuloItem ⭐ */}
          {articulosActuales.map((articulo, index) => (
            <ArticuloItem
              key={articulo.id_articulo || index}
              articulo={articulo}
              index={index}
              onUpdate={updateArticuloField}
              onRemove={removeArticulo}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Package size={48} className="mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">No hay artículos o servicios agregados aún.</p>
          <p className="text-sm text-gray-500 mt-1">Usa el buscador de arriba para agregar items.</p>
        </div>
      )}
    </div>
  );
};


const SolicitudesGestionView = () => {
  const { notify, confirm, ModalRenderer } = useModal();
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudesFiltradas, setSolicitudesFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({ estados: {}, vias: [] });

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaStatusFiltro, setCategoriaStatusFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [empresaFiltro, setEmpresaFiltro] = useState('');
  const [demoradoFiltro, setDemoradoFiltro] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

   const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      const response = await solicitudesService.getAll({});
      
      // ⭐ Cargar artículos para cada solicitud ⭐
      const solicitudesConArticulos = await Promise.all(
        response.data.map(async (solicitud) => {
          try {
            const articulosResponse = await solicitudesService.getArticulos(solicitud.id);
            return {
              ...solicitud,
              articulos: articulosResponse.data || []
            };
          } catch (error) {
            console.error(`Error al cargar artículos de solicitud ${solicitud.id}:`, error);
            return {
              ...solicitud,
              articulos: []
            };
          }
        })
      );
      
      setSolicitudes(solicitudesConArticulos);
      setSolicitudesFiltradas(solicitudesConArticulos);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      await notify('error', 'Error', error.message || 'Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const cargarStats = async () => {
    try {
      const response = await solicitudesService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const cargarConfig = async () => {
  try {
    const response = await solicitudesService.getConfig();
    setConfig(response.data);
  } catch (error) {
    console.error('Error al cargar configuración:', error);
    // Fallback: configuración por defecto
    setConfig({
      estados: {
        pendiente: [
          { value: 'solicitud_nueva', label: 'Solicitud Nueva', color: '#3B82F6' }
        ],
        en_curso: [
          { value: 'en_proceso', label: 'En Proceso', color: '#F59E0B' }
        ],
        completado: [
          { value: 'completada', label: 'Completada', color: '#10B981' }
        ]
      },
      vias: [
        { value: 'personal', label: 'Entrega Personal' },
        { value: 'envio', label: 'Envío' },
        { value: 'recogida', label: 'Recogida en Almacén' }
      ]
    });
  }
};

  useEffect(() => {
    cargarSolicitudes();
    cargarStats();
    cargarConfig();
  }, []);

  useEffect(() => {
    let resultado = [...solicitudes];

    // Filtro por búsqueda
    if (searchTerm) {
      resultado = resultado.filter(s =>
        s.folio.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.nombre_solicitud.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.empresa_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.cliente_nombre && s.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro por categoría de status
    if (categoriaStatusFiltro) {
      resultado = resultado.filter(s => s.categoria_status === categoriaStatusFiltro);
    }

    // Filtro por status específico
    if (statusFiltro) {
      resultado = resultado.filter(s => s.status === statusFiltro);
    }

    // Filtro por empresa
    if (empresaFiltro) {
      resultado = resultado.filter(s => s.id_empresa === parseInt(empresaFiltro));
    }

    // Filtro por demorado
    if (demoradoFiltro) {
      resultado = resultado.filter(s => s.demorado === (demoradoFiltro === 'true' ? 1 : 0));
    }

    // Ordenamiento
    resultado.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setSolicitudesFiltradas(resultado);
    setCurrentPage(1);
  }, [searchTerm, categoriaStatusFiltro, statusFiltro, empresaFiltro, demoradoFiltro, solicitudes, sortColumn, sortDirection]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = solicitudesFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(solicitudesFiltradas.length / itemsPerPage);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleNuevaSolicitud = () => {
    setModalMode('create');
    setSolicitudSeleccionada(null);
    setShowModal(true);
  };

  const handleEditarSolicitud = (solicitud) => {
    setModalMode('edit');
    setSolicitudSeleccionada(solicitud);
    setShowModal(true);
  };

  const handleVerSolicitud = (solicitud) => {
    setModalMode('view');
    setSolicitudSeleccionada(solicitud);
    setShowModal(true);
  };

  const handleEliminarSolicitud = async (id) => {
    const ok = await confirm('Eliminar solicitud', '¿Estás seguro de eliminar esta solicitud?');
    if (ok) {
      try {
        await solicitudesService.delete(id);
        await notify('success', 'Éxito', 'Solicitud eliminada exitosamente');
        cargarSolicitudes();
        cargarStats();
      } catch (error) {
        await notify('error', 'Error', error.message || 'Error al eliminar solicitud');
      }
    }
  };

  const getStatusLabel = (status) => {
    for (const categoria in config.estados) {
      const estado = config.estados[categoria].find(e => e.value === status);
      if (estado) return estado.label;
    }
    return status;
  };

  const getStatusColor = (status) => {
    for (const categoria in config.estados) {
      const estado = config.estados[categoria].find(e => e.value === status);
      if (estado) return estado.color;
    }
    return '#6B7280';
  };

  const getCategoriaStatusColor = (categoria) => {
    const colores = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_curso: 'bg-blue-100 text-blue-800',
      completado: 'bg-green-100 text-green-800'
    };
    return colores[categoria] || 'bg-gray-100 text-gray-800';
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Obtener lista única de empresas para el filtro
  const empresasUnicas = [...new Map(solicitudes.map(s => [s.id_empresa, { id: s.id_empresa, nombre: s.empresa_nombre }])).values()];

  return (
    <div>
      
       {/* Header responsivo */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl lg:text-2xl font-bold text-gray-800">Gestión de Solicitudes</h2>
          <p className="text-sm lg:text-base text-gray-600">Administra todas las solicitudes del sistema</p>
        </div>
        <button 
          onClick={handleNuevaSolicitud}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:bg-blue-700 w-full sm:w-auto"
        >
          <Plus size={20} /> Nueva Solicitud
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <FileText className="text-gray-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pendientes}</p>
              </div>
              <Clock className="text-yellow-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">En Curso</p>
                <p className="text-2xl font-bold text-blue-600">{stats.en_curso}</p>
              </div>
              <Package className="text-blue-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Completadas</p>
                <p className="text-2xl font-bold text-green-600">{stats.completadas}</p>
              </div>
              <CheckCircle className="text-green-500" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Demoradas</p>
                <p className="text-2xl font-bold text-red-600">{stats.demoradas}</p>
              </div>
              <AlertCircle className="text-red-500" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 lg:gap-4">
          <input 
            type="text" 
            placeholder="Buscar por folio, nombre, empresa..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
          <select 
            value={categoriaStatusFiltro}
            onChange={(e) => setCategoriaStatusFiltro(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todas las categorías</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_curso">En Curso</option>
            <option value="completado">Completado</option>
          </select>
          <select 
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todos los estados</option>
            {Object.entries(config.estados).map(([categoria, estados]) => (
              <optgroup key={categoria} label={categoria.toUpperCase()}>
                {estados.map(estado => (
                  <option key={estado.value} value={estado.value}>{estado.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <select 
            value={empresaFiltro}
            onChange={(e) => setEmpresaFiltro(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Todas las empresas</option>
            {empresasUnicas.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.nombre}</option>
            ))}
          </select>
          <select 
            value={demoradoFiltro}
            onChange={(e) => setDemoradoFiltro(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Demoradas/No Demoradas</option>
            <option value="true">Solo Demoradas</option>
            <option value="false">No Demoradas</option>
          </select>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </select>
        </div>
      </div>

        {/* Tabla */}
      <div className="hidden lg:block bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase"></th>
                <th onClick={() => handleSort('folio')} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200">
                  Folio <SortIcon column="folio" />
                </th>
                <th onClick={() => handleSort('nombre_solicitud')} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200">
                  Nombre <SortIcon column="nombre_solicitud" />
                </th>

                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Empresa</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cliente</th>

                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Servicio</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Sub Servicio</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Categoría</th>
                
                {/* ⭐ COLUMNA MODIFICADA: ARTÍCULOS/SERVICIOS ⭐ */}
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Artículos/Servicios</th>
                
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Asignado a</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Creado por</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Estado</th>
                <th onClick={() => handleSort('fecha_creacion')} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-200">
                  Fecha <SortIcon column="fecha_creacion" />
                </th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {currentItems.map(solicitud => {
                const esImagen = solicitud.tipo_archivo_principal?.startsWith('image/');
                const tieneArchivos = solicitud.total_archivos > 0;
                
                const getFileIconInfo = () => {
                  if (!solicitud.archivo_principal) return null;
                  const extension = solicitud.archivo_principal.split('.').pop().toLowerCase();
                  if (extension === 'pdf') return { icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF' };
                  if (extension === 'doc' || extension === 'docx') return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'DOC' };
                  if (extension === 'xls' || extension === 'xlsx') return { icon: FileText, color: 'text-green-600', bg: 'bg-green-50', label: 'XLS' };
                  return { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50', label: extension.toUpperCase() };
                };
                
                const fileInfo = getFileIconInfo();
                const FileIcon = fileInfo?.icon;
                
                return (
                  <tr key={solicitud.id} className="hover:bg-gray-50">
                    {/* Icono de demorado */}
                    <td className="px-6 py-4">
                      {solicitud.demorado === 1 && (
                        <AlertCircle size={20} className="text-red-500" title="Solicitud demorada" />
                      )}
                    </td>
                    
                  
                    
                    <td className="px-6 py-4 font-bold text-blue-600">{solicitud.folio}</td>
                    <td className="px-6 py-4 font-semibold text-gray-800">{solicitud.nombre_solicitud}</td>
                    <td className="px-6 py-4 text-gray-600">{solicitud.empresa_nombre}</td>
                    <td className="px-6 py-4">
                      {solicitud.cliente_nombre ? (
                        <div className="flex items-center gap-2">
                          {solicitud.origen === 'portal' ? (
                            <>
                              <User size={16} className="text-blue-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-800">{solicitud.cliente_nombre}</p>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mt-1">
                                  📝 Público
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <User size={16} className="text-green-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-800">{solicitud.cliente_nombre}</p>
                                {solicitud.cliente_email && (
                                  <p className="text-xs text-gray-500">{solicitud.cliente_email}</p>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm italic">Sin cliente</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.servicio_color }}>
                        {solicitud.servicio_nombre}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.subservicio_color }}>
                        {solicitud.subservicio_nombre}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.categoria_color }}>
                        {solicitud.categoria_nombre}
                      </span>
                    </td>
                    
                    {/* ⭐ COLUMNA DE ARTÍCULOS/SERVICIOS MEJORADA ⭐ */}
                    <td className="px-6 py-4">
                      {solicitud.articulos && solicitud.articulos.length > 0 ? (
                        <div className="space-y-2">
                          {solicitud.articulos.map((art, index) => {
                            const esServicio = art.tipo === 'servicio';
                            const stockInsuficiente = !esServicio && art.cantidad > art.stock_actual;
                            const stockBajo = !esServicio && art.stock_actual <= art.stock_minimo;
                            
                            return (
                              <div 
                                key={index} 
                                className={`text-sm p-2 rounded-lg border ${
                                  esServicio ? 'bg-purple-50 border-purple-200' :
                                  stockInsuficiente ? 'bg-red-50 border-red-200' :
                                  stockBajo ? 'bg-yellow-50 border-yellow-200' :
                                  'bg-green-50 border-green-200'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-800 truncate">{art.nombre}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      {esServicio ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-200 text-purple-800 text-xs font-semibold rounded">
                                          <Coffee size={12} />
                                          SERVICIO
                                        </span>
                                      ) : (
                                        <>
                                          <span className="text-xs text-gray-600">
                                            Cant: <strong className="text-blue-600">{art.cantidad}</strong>
                                          </span>
                                          <span className="text-xs text-gray-400">|</span>
                                          <span className="text-xs text-gray-600">
                                            Stock: <strong className={
                                              art.stock_actual === 0 ? 'text-red-600' :
                                              stockBajo ? 'text-yellow-600' :
                                              'text-green-600'
                                            }>{art.stock_actual}</strong>
                                          </span>
                                        </>
                                      )}
                                    </div>
                                    
                                    {/* Badges de estado */}
                                    {!esServicio && (
                                      <div className="mt-1">
                                        {stockInsuficiente ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-200 text-red-800 text-xs font-semibold rounded">
                                            <AlertCircle size={12} />
                                            Falta: {art.cantidad - art.stock_actual}
                                          </span>
                                        ) : stockBajo ? (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs font-semibold rounded">
                                            <AlertTriangle size={12} />
                                            Stock bajo
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-200 text-green-800 text-xs font-semibold rounded">
                                            <CheckCircle size={12} />
                                            Disponible
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm italic">Sin artículos</span>
                      )}
                    </td>
                    
                    {/* Resto de columnas igual... */}
                    <td className="px-6 py-4">
                      {solicitud.usuario_asignado_nombre ? (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-800">{solicitud.usuario_asignado_nombre}</p>
                            <p className="text-xs text-gray-500">{solicitud.usuario_asignado_email}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin asignar</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{solicitud.usuario_creador_nombre}</p>
                          <p className="text-xs text-gray-500">{solicitud.usuario_creador_email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoriaStatusColor(solicitud.categoria_status)}`}>
                          {solicitud.categoria_status === 'pendiente' && 'Pendiente'}
                          {solicitud.categoria_status === 'en_curso' && 'En Curso'}
                          {solicitud.categoria_status === 'completado' && 'Completado'}
                        </span>
                        <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: getStatusColor(solicitud.status) }}>
                          {getStatusLabel(solicitud.status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {new Date(solicitud.fecha_creacion).toLocaleDateString('es-ES')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleVerSolicitud(solicitud)} className="text-blue-600 hover:text-blue-800" title="Ver detalles">
                          <Eye size={18} />
                        </button>
                        <button onClick={() => handleEditarSolicitud(solicitud)} className="text-green-600 hover:text-green-800" title="Editar">
                          <Edit size={18} />
                        </button>
                        <button onClick={() => handleEliminarSolicitud(solicitud.id)} className="text-red-600 hover:text-red-800" title="Eliminar">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
 {/* Vista móvil (tarjetas) */}
      <div className="lg:hidden space-y-4">
        {currentItems.map(solicitud => (
          <div key={solicitud.id} className="bg-white rounded-lg shadow-md p-4">
            {/* Header de la tarjeta */}
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                {solicitud.demorado === 1 && (
                  <AlertCircle size={16} className="text-red-500" />
                )}
                <span className="font-bold text-blue-600">{solicitud.folio}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleVerSolicitud(solicitud)} className="text-blue-600">
                  <Eye size={18} />
                </button>
                <button onClick={() => handleEditarSolicitud(solicitud)} className="text-green-600">
                  <Edit size={18} />
                </button>
                <button onClick={() => handleEliminarSolicitud(solicitud.id)} className="text-red-600">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Contenido */}
            <h3 className="font-semibold text-gray-800 mb-2">{solicitud.nombre_solicitud}</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-gray-500" />
                <span className="text-gray-600">{solicitud.empresa_nombre}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.servicio_color }}>
                  {solicitud.servicio_nombre}
                </span>
                <span className="px-2 py-1 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.categoria_color }}>
                  {solicitud.categoria_nombre}
                </span>
              </div>

              {/* Artículos */}
              {solicitud.articulos && solicitud.articulos.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-semibold text-gray-600">Artículos/Servicios:</p>
                  {solicitud.articulos.map((art, index) => {
                    const esServicio = art.tipo === 'servicio';
                    const stockInsuficiente = !esServicio && art.cantidad > art.stock_actual;
                    
                    return (
                      <div 
                        key={index}
                        className={`text-xs p-2 rounded border ${
                          esServicio ? 'bg-purple-50 border-purple-200' :
                          stockInsuficiente ? 'bg-red-50 border-red-200' :
                          'bg-green-50 border-green-200'
                        }`}
                      >
                        <p className="font-semibold">{art.nombre}</p>
                        {esServicio ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-200 text-purple-800 rounded mt-1">
                            <Coffee size={10} />
                            SERVICIO
                          </span>
                        ) : (
                          <div className="flex items-center gap-2 mt-1">
                            <span>Cant: <strong>{art.cantidad}</strong></span>
                            <span>Stock: <strong className={stockInsuficiente ? 'text-red-600' : 'text-green-600'}>
                              {art.stock_actual}
                            </strong></span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Asignado y creador */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <User size={14} className="text-blue-600" />
                <span className="text-gray-600">{solicitud.usuario_asignado_nombre || 'Sin asignar'}</span>
              </div>

              {/* Estado y fecha */}
              <div className="flex justify-between items-center pt-2">
                <span className={`px-2 py-1 rounded text-xs font-semibold ${getCategoriaStatusColor(solicitud.categoria_status)}`}>
                  {solicitud.categoria_status === 'pendiente' && 'Pendiente'}
                  {solicitud.categoria_status === 'en_curso' && 'En Curso'}
                  {solicitud.categoria_status === 'completado' && 'Completado'}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(solicitud.fecha_creacion).toLocaleDateString('es-ES')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

       {/* Paginación responsiva */}
      <div className="bg-white rounded-lg shadow mt-4 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, solicitudesFiltradas.length)} de {solicitudesFiltradas.length}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 text-sm"
            >
              Primero
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 text-sm"
            >
              Ant.
            </button>
            <span className="px-3 py-1 text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 text-sm"
            >
              Sig.
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 text-sm"
            >
              Último
            </button>
          </div>
        </div>
      </div>


      {/* Modal */}
      {showModal && (
        <SolicitudModal
          mode={modalMode}
          solicitud={solicitudSeleccionada}
          config={config}
          onClose={() => {
            setShowModal(false);
            setSolicitudSeleccionada(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setSolicitudSeleccionada(null);
            cargarSolicitudes();
            cargarStats();
          }}
        />
      )}
      {ModalRenderer}
    </div>
  );
};


// ==================== MODAL DE SOLICITUD ====================
const SolicitudModal = ({ mode, solicitud, config, onClose, onSuccess }) => {
  const { notify, confirm, ModalRenderer } = useModal();
      const [formData, setFormData] = useState({
      nombre_solicitud: '',
      descripcion: '',
      id_servicio: '',
      id_subservicio: '',
      id_categoria: '',
      articulos_seleccionados: [], // ← CAMBIAR de id_articulo a esto
      id_empresa: '',
      id_cliente: '',
      id_usuario_asignado: ''
    });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Datos para selects
  const [servicios, setServicios] = useState([]);
  const [subservicios, setSubservicios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [stockArticulo, setStockArticulo] = useState(null);
  const [cargandoStock, setCargandoStock] = useState(false);

  // Manejo de archivos
  const [archivos, setArchivos] = useState([]);
  const [archivosSubidos, setArchivosSubidos] = useState([]);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);

  const [articulosConCantidad, setArticulosConCantidad] = useState([]);
  // Mantener ref sincronizado con el estado
  useEffect(() => {
    articulosConCantidadRef.current = articulosConCantidad;
  }, [articulosConCantidad]);
  const [cargandoArticulos, setCargandoArticulos] = useState(false);
  const selectArticulosRef = useRef(null);
  // ⭐ Ref para evitar stale closure en handleArticulosChange
  const articulosConCantidadRef = useRef([]);

  // ⭐ DEFINIR isViewMode AL INICIO
  const isViewMode = mode === 'view';

  // ⭐ CARGAR DATOS INICIALES (selects)
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // ⭐ CARGAR DATOS DE LA SOLICITUD (cuando se abre en modo edit/view)
  useEffect(() => {
    if (solicitud && (mode === 'edit' || mode === 'view')) {
      // Cargar datos básicos del formulario
      setFormData({
        nombre_solicitud: solicitud.nombre_solicitud || '',
        descripcion: solicitud.descripcion || '',
        id_servicio: solicitud.id_servicio || '',
        id_subservicio: solicitud.id_subservicio || '',
        id_categoria: solicitud.id_categoria || '',
        id_empresa: solicitud.id_empresa || '',
        id_cliente: solicitud.id_cliente || '',
        status: solicitud.status || 'solicitud_nueva',
        via: solicitud.via || '',
        tiempo_entrega: solicitud.tiempo_entrega ? solicitud.tiempo_entrega.split('T')[0] : '',
        id_usuario_asignado: solicitud.id_usuario_asignado || ''
      });

      // Cargar archivos
      cargarArchivos(solicitud.id);

      // Cargar artículos de la solicitud
      cargarArticulosSolicitud(solicitud.id);
    }
  }, [solicitud, mode]);

  // ⭐ CARGAR ARTÍCULOS DE LA SOLICITUD
  const cargarArticulosSolicitud = async (solicitudId) => {
    try {
      const response = await solicitudesService.getArticulos(solicitudId);
      
      if (response.data && response.data.length > 0) {
        const articulosCargados = response.data.map(item => ({
          id_articulo: item.id_articulo,
          nombre: item.nombre,
          tipo: item.tipo,
          cantidad: item.cantidad,
          stock: item.stock_actual || 0,
          stock_minimo: item.stock_minimo || 0,
          status: item.status || 'solicitud_nueva',
          via: item.via || '',
          tiempo_entrega: item.tiempo_entrega ? item.tiempo_entrega.split('T')[0] : '',
          observacion: item.observacion || ''
        }));
        
        // ⭐ Actualizar ref ANTES de setear estado y disparar Select2
        articulosConCantidadRef.current = articulosCargados;
        setArticulosConCantidad(articulosCargados);

        // Actualizar Select2 si no es modo vista
        if (mode === 'edit' && articulos.length > 0) {
          setTimeout(() => {
            const $select = $(selectArticulosRef.current);
            const ids = articulosCargados.map(a => a.id_articulo.toString());
            if ($select.data('select2')) {
              $select.val(ids).trigger('change');
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  useEffect(() => {
    const cargarArticulosSolicitud = async () => {
      if (solicitud && solicitud.id && (mode === 'edit' || mode === 'view')) {
        setCargandoArticulos(true);
        try {
          const response = await solicitudesService.getArticulos(solicitud.id);
          
          if (response.data && response.data.length > 0) {
            const articulosCargados = response.data.map(item => ({
              id_articulo: item.id_articulo,
              nombre: item.nombre,
              tipo: item.tipo,
              cantidad: item.cantidad,
              stock: item.stock_actual || 0,
              stock_minimo: item.stock_minimo || 0,
              status: item.status || 'solicitud_nueva',
              via: item.via || '',
              tiempo_entrega: item.tiempo_entrega ? item.tiempo_entrega.split('T')[0] : '',
              observacion: item.observacion || ''
            }));
            
            // ⭐ Actualizar ref ANTES de setear estado y disparar Select2
            articulosConCantidadRef.current = articulosCargados;
            setArticulosConCantidad(articulosCargados);
            
            // Actualizar Select2 si no es modo vista
            if (mode === 'edit') {
              setTimeout(() => {
                const $select = $(selectArticulosRef.current);
                const ids = articulosCargados.map(a => a.id_articulo.toString());
                if ($select.data('select2')) {
                  $select.val(ids).trigger('change');
                }
              }, 100);
            }
          }
        } catch (error) {
          console.error('Error al cargar artículos:', error);
          await notify('error', 'Error', 'Error al cargar los artículos de la solicitud');
        } finally {
          setCargandoArticulos(false);
        }
      }
    };

    cargarArticulosSolicitud();
  }, [solicitud, mode]);

  // ⭐ NUEVA función para cargar stock
  const cargarStockArticulo = async (articuloId) => {
    setCargandoStock(true);
    try {
      const response = await articulosService.getStock(articuloId);
      setStockArticulo(response.data);
    } catch (error) {
      console.error('Error al cargar stock:', error);
      setStockArticulo(null);
    } finally {
      setCargandoStock(false);
    }
  };

  const cargarDatosIniciales = async () => {
    try {
      const [
        resServicios,
        resSubservicios,
        resCategorias,
        resArticulos,
        resEmpresas,
        resClientes,
        resUsuarios
      ] = await Promise.all([
        serviciosService.getAll({ activo: true }),
        subserviciosService.getAll({ activo: true }),
        categoriasSolicitudService.getAll({ activo: true }),
        articulosService.getAll({}),
        empresasService.getAll({ activo: true }),
        clientesService.getAll({ activo: true }),
        usuariosService.getAll()
      ]);
      
      setServicios(resServicios.data);
      setSubservicios(resSubservicios.data);
      setCategorias(resCategorias.data);
      setArticulos(resArticulos.data);
      setEmpresas(resEmpresas.data);
      setClientes(resClientes.data);
      setUsuarios(resUsuarios.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const cargarArchivos = async (solicitudId) => {
    try {
      const response = await solicitudesArchivosService.getBySolicitudId(solicitudId);
      setArchivosSubidos(response.data);
    } catch (error) {
      console.error('Error al cargar archivos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    if (archivosSubidos.length + archivos.length + files.length > 3) {
      await notify('warning', 'Atención', 'Solo se permiten máximo 3 archivos');
      return;
    }

    const archivosValidos = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        await notify('warning', 'Archivo muy grande', `El archivo ${file.name} supera los 5MB`);
        continue;
      }
      archivosValidos.push(file);
    }

    setArchivos(prev => [...prev, ...archivosValidos]);
  };

  const eliminarArchivo = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const eliminarArchivoSubido = async (archivoId) => {
    const ok = await confirm('Eliminar archivo', '¿Eliminar este archivo?');
    if (!ok) return;

    try {
      await solicitudesArchivosService.delete(archivoId);
      setArchivosSubidos(prev => prev.filter(a => a.id !== archivoId));
      await notify('success', 'Éxito', 'Archivo eliminado');
    } catch (error) {
      await notify('error', 'Error', 'Error al eliminar archivo');
    }
  };

  const subirArchivos = async (solicitudId) => {
    if (archivos.length === 0) return;

    setSubiendoArchivo(true);
    
    try {
      for (const archivo of archivos) {
        await solicitudesArchivosService.upload(solicitudId, archivo);
      }
      setArchivos([]);
    } catch (error) {
      console.error('Error al subir archivos:', error);
      throw error;
    } finally {
      setSubiendoArchivo(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  if (!formData.nombre_solicitud.trim()) {
    setError('El nombre de la solicitud es requerido');
    setLoading(false);
    return;
  }

  if (!formData.id_servicio || !formData.id_categoria || !formData.id_empresa) {
    setError('Servicio, Categoría y Empresa son requeridos');
    setLoading(false);
    return;
  }

  try {
    const dataToSend = {
      ...formData,
      id_subservicio: formData.id_subservicio || null,
      articulos: articulosConCantidad, // ← ENVIAR ARRAY DE ARTÍCULOS
      id_cliente: formData.id_cliente || null,
      via: formData.via || null,
      tiempo_entrega: formData.tiempo_entrega || null,
      id_usuario_asignado: formData.id_usuario_asignado || null
    };

    let solicitudId;

    if (mode === 'create') {
      const response = await solicitudesService.create(dataToSend);
      solicitudId = response.data.id;
      
      if (archivos.length > 0) {
        await subirArchivos(solicitudId);
      }
      
      await notify('success', 'Éxito', `Solicitud creada exitosamente\nFolio: ${response.data.folio}`);
    } else if (mode === 'edit') {
      await solicitudesService.update(solicitud.id, dataToSend);
      solicitudId = solicitud.id;

      if (archivos.length > 0) {
        await subirArchivos(solicitudId);
      }

      await notify('success', 'Éxito', 'Solicitud actualizada exitosamente');
    }
    
    onSuccess();
  } catch (err) {
    setError(err.response?.data?.message || err.message || 'Error al guardar solicitud');
  } finally {
    setLoading(false);
  }
};

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (tipoArchivo, nombreArchivo) => {
    const extension = nombreArchivo.split('.').pop().toLowerCase();
    
    if (tipoArchivo?.includes('pdf') || extension === 'pdf') {
      return { icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF' };
    }
    
    if (tipoArchivo?.includes('word') || extension === 'doc' || extension === 'docx') {
      return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'WORD' };
    }
    
    if (tipoArchivo?.includes('sheet') || extension === 'xls' || extension === 'xlsx') {
      return { icon: FileText, color: 'text-green-600', bg: 'bg-green-50', label: 'EXCEL' };
    }
    
    return { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50', label: extension.toUpperCase() };
  };

    useEffect(() => {
      if (!isViewMode && articulos.length > 0) {
        const $select = $(selectArticulosRef.current);
        
        // Destruir Select2 previo si existe
        if ($select.data('select2')) {
          $select.select2('destroy');
        }

        // Inicializar Select2
        $select.select2({
          placeholder: 'Buscar y seleccionar artículos...',
          allowClear: true,
          width: '100%',
          language: {
            noResults: () => 'No se encontraron artículos',
            searching: () => 'Buscando...'
          }
        });

        // Manejar cambios
        $select.on('change', function() {
          const selectedIds = $(this).val() || [];
          handleArticulosChange(selectedIds);
        });

        // Cleanup
        return () => {
          if ($select.data('select2')) {
            $select.select2('destroy');
          }
        };
      }
    }, [articulos, isViewMode]);

  const handleArticulosChange = useCallback((selectedIds) => {
  // ⭐ Usar ref para evitar stale closure — siempre tiene el valor más reciente
  const currentArticulos = articulosConCantidadRef.current;
  
  const nuevosArticulos = selectedIds.map(id => {
    const articulo = articulos.find(a => a.id === parseInt(id));
    const articuloExistente = currentArticulos.find(a => a.id_articulo === parseInt(id));
    
    return {
      id_articulo: parseInt(id),
      nombre: articulo?.nombre || '',
      tipo: articulo?.tipo || 'articulo',
      cantidad: articuloExistente?.cantidad || 1,
      stock: articulo?.stock_actual || 0,
      stock_minimo: articulo?.stock_minimo || 0,
      // Preservar campos editados por el usuario
      status: articuloExistente?.status || 'solicitud_nueva',
      via: articuloExistente?.via || '',
      tiempo_entrega: articuloExistente?.tiempo_entrega || '',
      observacion: articuloExistente?.observacion || ''
    };
  });
  
  setArticulosConCantidad(nuevosArticulos);
}, [articulos]);

  const handleCantidadChange = (id_articulo, cantidad) => {
    setArticulosConCantidad(prev =>
      prev.map(art =>
        art.id_articulo === id_articulo
          ? { ...art, cantidad: parseInt(cantidad) || 1 }
          : art
      )
    );
  };

  const eliminarArticulo = (id_articulo) => {
    setArticulosConCantidad(prev => prev.filter(a => a.id_articulo !== id_articulo));
    
    // Actualizar Select2
    const $select = $(selectArticulosRef.current);
    const currentValues = $select.val() || [];
    const newValues = currentValues.filter(id => parseInt(id) !== id_articulo);
    $select.val(newValues).trigger('change');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">
              {mode === 'create' && '📝 Nueva Solicitud'}
              {mode === 'edit' && '✏️ Editar Solicitud'}
              {mode === 'view' && '👁️ Detalles de la Solicitud'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Folio */}
          {solicitud && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText size={24} className="text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Folio</p>
                  <p className="text-xl font-bold text-blue-600">{solicitud.folio}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Nombre de la solicitud */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Nombre de la Solicitud <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nombre_solicitud"
                value={formData.nombre_solicitud}
                onChange={handleChange}
                disabled={isViewMode}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: Compra de repuestos para mantenimiento"
              />
            </div>

            {/* Fila 1: Servicio, Subservicio, Categoría */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* (mantener igual) */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Servicio <span className="text-red-500">*</span>
                </label>
                <select
                  name="id_servicio"
                  value={formData.id_servicio}
                  onChange={handleChange}
                  disabled={isViewMode}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar...</option>
                  {servicios.map(srv => (
                    <option key={srv.id} value={srv.id}>{srv.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Subservicio
                </label>
                <select
                  name="id_subservicio"
                  value={formData.id_subservicio}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Ninguno</option>
                  {subservicios.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Categoría <span className="text-red-500">*</span>
                </label>
                <select
                  name="id_categoria"
                  value={formData.id_categoria}
                  onChange={handleChange}
                  disabled={isViewMode}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar...</option>
                  {categorias.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
            </div>
          </div>

        {/* Fila 2: Empresa, Cliente */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Empresa */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Empresa/Zona <span className="text-red-500">*</span>
          </label>
          <select
            name="id_empresa"
            value={formData.id_empresa}
            onChange={handleChange}
            disabled={isViewMode}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Seleccionar...</option>
            {empresas.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.nombre}</option>
            ))}
          </select>
        </div>

        {/* Cliente */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Cliente (Opcional)
          </label>
          <select
            name="id_cliente"
            value={formData.id_cliente}
            onChange={handleChange}
            disabled={isViewMode}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Ninguno</option>
            {clientes.map(cli => (
              <option key={cli.id} value={cli.id}>{cli.nombre}</option>
            ))}
          </select>
        </div>
      </div>

{/* ========== GESTIÓN DE ARTÍCULOS/SERVICIOS ========== */}
{!isViewMode && (
  <ArticulosSolicitudManager
    articulos={articulosConCantidad}
    onArticulosChange={setArticulosConCantidad}
    articulosDisponibles={articulos}
    notify={notify}
  />
)}

{/* ========== VISTA DE ARTÍCULOS CON IMÁGENES (MODO VIEW) ========== */}
{isViewMode && articulosConCantidad.length > 0 && (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-semibold text-xl text-gray-800 flex items-center gap-2">
      <Package size={24} className="text-blue-600" />
      Artículos/Servicios en esta Solicitud ({articulosConCantidad.length})
    </h4>
    
    {cargandoArticulos ? (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="text-gray-600 mt-3">Cargando artículos...</p>
      </div>
    ) : (
      <div className="space-y-4">
        {articulosConCantidad.map((articulo, index) => (
          <ArticuloItem
            key={articulo.id_articulo || index}
            articulo={articulo}
            index={index}
            isViewMode={true}
          />
        ))}
      </div>
    )}
  </div>
)}

{/* Mensaje si no hay artículos */}
{isViewMode && articulosConCantidad.length === 0 && !cargandoArticulos && (
  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mt-4">
    <Package size={64} className="mx-auto text-gray-400 mb-4" />
    <p className="text-gray-600 font-semibold text-lg">No hay artículos en esta solicitud</p>
    <p className="text-sm text-gray-500 mt-2">Esta solicitud aún no tiene artículos o servicios agregados.</p>
  </div>
)}

            {/* ⭐ INDICADOR DE STOCK EN TIEMPO REAL */}
            {formData.id_articulo && stockArticulo && (
              <div className={`p-4 rounded-lg border-2 ${
                formData.cantidad > stockArticulo.stock 
                  ? 'bg-red-50 border-red-300' 
                  : formData.cantidad > (stockArticulo.stock - stockArticulo.stock_minimo)
                  ? 'bg-yellow-50 border-yellow-300'
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-center gap-3">
                  <Package size={24} className={
                    formData.cantidad > stockArticulo.stock 
                      ? 'text-red-600' 
                      : formData.cantidad > (stockArticulo.stock - stockArticulo.stock_minimo)
                      ? 'text-yellow-600'
                      : 'text-green-600'
                  } />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">
                      {stockArticulo.nombre}
                    </p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className="text-gray-600">
                        Stock disponible: <strong className={
                          stockArticulo.stock === 0 ? 'text-red-600' : 
                          stockArticulo.stock <= stockArticulo.stock_minimo ? 'text-yellow-600' : 
                          'text-green-600'
                        }>{stockArticulo.articulo_stock}</strong>
                      </span>
                      <span className="text-gray-600">
                        Solicitando: <strong className="text-blue-600">{formData.cantidad}</strong>
                      </span>
                      <span className="text-gray-600">
                        Faltante: <strong className={
                          (formData.cantidad - stockArticulo.stock) > 0 ? 'text-red-600' : 'text-green-600'
                        }>
                          {Math.max(0, formData.cantidad - stockArticulo.stock)}
                        </strong>
                      </span>
                    </div>
                  </div>
                  {formData.cantidad > stockArticulo.stock && (
                    <div className="text-right">
                      <AlertCircle size={24} className="text-red-600" />
                      <p className="text-xs text-red-600 font-semibold mt-1">Stock insuficiente</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {cargandoStock && formData.id_articulo && (
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <p className="text-blue-600 text-center">Cargando stock...</p>
              </div>
            )}

           
            {/* Usuario Asignado */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                👤 Asignar a Usuario
              </label>
              <select
                name="id_usuario_asignado"
                value={formData.id_usuario_asignado}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Sin asignar</option>
                {usuarios.map(usr => (
                  <option key={usr.id} value={usr.id}>
                    {usr.nombre} {usr.email ? `(${usr.email})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Observación / Lo que están solicitando
              </label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                disabled={isViewMode}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Describe detalladamente la solicitud..."
              />
            </div>

            {/* Archivos Adjuntos */}
            <div className="border-t pt-4">
              <label className="block text-gray-700 font-medium mb-2">
                📎 Archivos Adjuntos (Máximo 3)
              </label>
              
              {/* (mantener el código de archivos igual que antes) */}
              {archivosSubidos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Archivos actuales:</p>
                  <div className="grid grid-cols-3 gap-4">
                    {archivosSubidos.map(archivo => {
                      const esImagen = archivo.tipo_archivo?.startsWith('image/');
                      const urlArchivo = `${UPLOADS_BASE_URL}/uploads/solicitudes/${archivo.ruta_archivo}`;
                      const fileInfo = getFileIcon(archivo.tipo_archivo, archivo.nombre_archivo);
                      const IconComponent = fileInfo.icon;
                      
                      return (
                        <div key={archivo.id} className="relative group">
                          <div className="rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition">
                            {esImagen ? (
                              <div className="aspect-square">
                                <img
                                  src={urlArchivo}
                                  alt={archivo.nombre_archivo}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className={`aspect-square ${fileInfo.bg} flex flex-col items-center justify-center p-4`}>
                                <IconComponent size={48} className={fileInfo.color} />
                                <span className={`text-xs font-bold ${fileInfo.color} mt-2`}>
                                  {fileInfo.label}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-gray-800 truncate" title={archivo.nombre_archivo}>
                              {archivo.nombre_archivo}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(archivo.tamanio)}</p>
                          </div>
                          
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <a
                              href={urlArchivo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 shadow-lg"
                              title="Ver/Descargar">
                            
                              <Eye size={14} />
                            </a>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  eliminarArchivoSubido(archivo.id);
                                }}
                                className="bg-red-600 text-white p-1.5 rounded hover:bg-red-700 shadow-lg"
                                title="Eliminar"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {archivos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Archivos por subir:</p>
                  <div className="grid grid-cols-3 gap-4">
                    {archivos.map((archivo, index) => {
                      const esImagen = archivo.type?.startsWith('image/');
                      const previewUrl = esImagen ? URL.createObjectURL(archivo) : null;
                      const fileInfo = getFileIcon(archivo.type, archivo.name);
                      const IconComponent = fileInfo.icon;
                      
                      return (
                        <div key={index} className="relative group">
                          <div className="rounded-lg overflow-hidden border-2 border-yellow-400">
                            {esImagen ? (
                              <div className="aspect-square">
                                <img
                                  src={previewUrl}
                                  alt={archivo.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className={`aspect-square ${fileInfo.bg} flex flex-col items-center justify-center p-4`}>
                                <IconComponent size={48} className={fileInfo.color} />
                                <span className={`text-xs font-bold ${fileInfo.color} mt-2`}>
                                  {fileInfo.label}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-gray-800 truncate" title={archivo.name}>
                              {archivo.name}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(archivo.size)}</p>
                          </div>
                          
                          {!isViewMode && (
                            <button
                              type="button"
                              onClick={() => eliminarArchivo(index)}
                              className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded hover:bg-red-700 shadow-lg opacity-0 group-hover:opacity-100 transition"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isViewMode && (archivosSubidos.length + archivos.length < 3) && (
                <div>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos: Imágenes, PDF, Word, Excel. Máximo 5MB por archivo.
                  </p>
                </div>
              )}
            </div>

            {/* Info adicional en modo vista */}
            {isViewMode && solicitud && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar size={18} />
                  Información del Sistema
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">ID:</p>
                    <p className="font-semibold">{solicitud.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Creado por:</p>
                    <p className="font-semibold">{solicitud.usuario_creador_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fecha de creación:</p>
                    <p className="font-semibold">
                      {new Date(solicitud.fecha_creacion).toLocaleString('es-ES')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Última actualización:</p>
                    <p className="font-semibold">
                      {new Date(solicitud.updated_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                  {solicitud.usuario_asignado_nombre && (
                    <div>
                      <p className="text-gray-600">Asignado a:</p>
                      <p className="font-semibold">👤 {solicitud.usuario_asignado_nombre}</p>
                    </div>
                  )}
                  {solicitud.demorado === 1 && (
                    <div className="col-span-2">
                      <div className="bg-red-100 border border-red-300 rounded p-2 flex items-center gap-2">
                        <AlertCircle size={18} className="text-red-600" />
                        <p className="text-red-800 font-semibold">⚠️ Esta solicitud está demorada</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-4 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {isViewMode ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isViewMode && (
            <button
              onClick={handleSubmit}
              disabled={loading || subiendoArchivo}
              className={`px-6 py-2 rounded-lg text-white ${
                loading || subiendoArchivo
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Guardando...' : subiendoArchivo ? 'Subiendo archivos...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
      {ModalRenderer}
    </div>
  );


};



export default SolicitudesGestionView;